import { useEffect } from "react";
import type { ReactNode } from "react";
import { StyleSheet } from "react-native";
import Animated, {
  Easing,
  cancelAnimation,
  interpolateColor,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import Svg, { Circle, Path } from "react-native-svg";
import LPressable from "@components/LPressable";
import { CONTROL_HEIGHT, RADIUS, colors } from "./colors";
import { DISSOLVE_MS, EASE, ICON_IN_MS, MORPH_MS } from "./motion";
import type { SheetButtonIcon } from "./sheetButton";

/**
 * The Pay button's second life, native (the web's pay-status.tsx): once
 * pressed it becomes the payment's status. The spinner comes in beside the
 * words with a spring; the words change on the morph's curve; as the
 * checkout folds away the fill dissolves and the words stay as plain text in
 * the button's blue — spinner kept until the tickets are the buyer's, and
 * green (tick and words) only at "You're going". Every word, timing and
 * curve is the core's (checkoutView.ts reads them; motion.ts eases them).
 * A vendor's prepared checkout ends on its own marks instead (the web's):
 * `ready` in the button's blue, never the green of tickets issued, and
 * `stopped` in the muted grey.
 */
export function PayStatusButton({
  words,
  icon,
  plain,
  done,
  disabled,
  onPress,
}: {
  words: string;
  icon: SheetButtonIcon | null;
  /** From the fold on: the fill has gone, the words stand alone. */
  plain: boolean;
  done: boolean;
  disabled: boolean;
  onPress: () => void;
}) {
  // The fill leaves as a surface does: fading, drawing in a touch.
  const fill = useSharedValue(plain ? 0 : 1);
  useEffect(() => {
    fill.value = withTiming(plain ? 0 : 1, {
      duration: DISSOLVE_MS,
      easing: EASE.dissolve,
    });
  }, [plain, fill]);
  const fillStyle = useAnimatedStyle(() => ({
    opacity: fill.value,
    transform: [{ scale: 0.94 + 0.06 * fill.value }],
  }));

  // White on the fill; its blue once the fill has gone (a beat after it
  // starts to go, never white on a washed-out blue); green once done.
  const ink = useSharedValue(0);
  useEffect(() => {
    ink.value = plain
      ? withDelay(
          150,
          withTiming(done ? 2 : 1, {
            duration: DISSOLVE_MS - 150,
            easing: EASE.settle,
          }),
        )
      : withTiming(0, { duration: 200 });
  }, [plain, done, ink]);
  // A stopped checkout's words settle in the muted grey instead, on the
  // same beat.
  const stopped = icon === "stopped";
  const muted = useSharedValue(0);
  useEffect(() => {
    muted.value =
      plain && stopped
        ? withDelay(
            150,
            withTiming(1, { duration: DISSOLVE_MS - 150, easing: EASE.settle }),
          )
        : withTiming(0, { duration: 200 });
  }, [plain, stopped, muted]);
  const inkStyle = useAnimatedStyle(() => ({
    color: interpolateColor(
      muted.value,
      [0, 1],
      [
        interpolateColor(
          ink.value,
          [0, 1, 2],
          [colors.onPrimary, colors.primary, colors.successText],
        ),
        colors.secondary,
      ],
    ),
  }));

  // Pressed: answered on the finger's touch, as the web's :active.
  const pressed = useSharedValue(1);
  const pressStyle = useAnimatedStyle(() => ({
    transform: [{ scale: pressed.value }],
  }));
  // Any status takes the press away; only a moving one is busy.
  const moving = icon !== null;
  const busy = moving && icon !== "ready" && icon !== "stopped";

  return (
    <LPressable
      accessibilityLabel={words}
      accessibilityLiveRegion="polite"
      accessibilityRole="button"
      accessibilityState={{ disabled: disabled || moving, busy }}
      disabled={disabled || moving}
      onPress={onPress}
      onPressIn={() => pressed.set(withTiming(0.97, { duration: 160 }))}
      onPressOut={() => pressed.set(withTiming(1, { duration: 160 }))}
      sentryEventName="NativeCheckout.Pay"
      testID="checkout-pay"
    >
      <Animated.View
        style={[
          styles.button,
          { opacity: disabled && !moving ? 0.5 : 1 },
          pressStyle,
        ]}
      >
        <Animated.View style={[styles.fill, fillStyle]} />
        {icon ? (
          <IconIn key={icon}>
            {icon === "tick" ? (
              <DoneTick />
            ) : icon === "ready" ? (
              <ReadyMark color={plain ? colors.primary : colors.onPrimary} />
            ) : icon === "stopped" ? (
              <StoppedMark
                color={plain ? colors.secondary : colors.onPrimary}
              />
            ) : (
              // In the button's blue once its fill has gone.
              <Spinner color={plain ? colors.primary : colors.onPrimary} />
            )}
          </IconIn>
        ) : null}
        <Morph key={words}>
          <Animated.Text numberOfLines={1} style={[styles.words, inkStyle]}>
            {words}
          </Animated.Text>
        </Morph>
      </Animated.View>
    </LPressable>
  );
}

/** A status icon arriving: from small and clear to full, on a spring. */
function IconIn({ children }: { children: ReactNode }) {
  const shown = useSharedValue(0);
  useEffect(() => {
    shown.value = withTiming(1, { duration: ICON_IN_MS, easing: EASE.iconIn });
  }, [shown]);
  const style = useAnimatedStyle(() => ({
    opacity: Math.min(1, shown.value),
    transform: [{ scale: 0.6 + 0.4 * shown.value }],
  }));
  return <Animated.View style={style}>{children}</Animated.View>;
}

/** New words settling in on the morph's curve (a slight overshoot). */
function Morph({ children }: { children: ReactNode }) {
  const shown = useSharedValue(0);
  useEffect(() => {
    shown.value = withTiming(1, { duration: MORPH_MS, easing: EASE.morph });
  }, [shown]);
  const style = useAnimatedStyle(() => ({
    opacity: Math.min(1, shown.value * 1.6),
    transform: [{ translateY: 4 * (1 - shown.value) }],
  }));
  return (
    <Animated.View style={[styles.morph, style]}>{children}</Animated.View>
  );
}

const ICON = 22;

/** One spinner turns through processing, accepted and issuing. */
function Spinner({ color }: { color: string }) {
  const turn = useSharedValue(0);
  useEffect(() => {
    turn.value = withRepeat(
      withTiming(360, { duration: 600, easing: Easing.linear }),
      -1,
    );
    return () => cancelAnimation(turn);
  }, [turn]);
  const style = useAnimatedStyle(() => ({
    transform: [{ rotate: `${turn.value}deg` }],
  }));
  return (
    <Animated.View style={style}>
      <Svg width={ICON} height={ICON} viewBox="0 0 23 23" fill="none">
        <Path
          d="M21.313 11.4062C21.313 16.8775 16.8777 21.3128 11.4065 21.3128"
          stroke={color}
          strokeWidth={3}
          strokeLinecap="round"
        />
        <Circle
          cx={11.4065}
          cy={11.4065}
          r={9.9065}
          stroke={color}
          strokeOpacity={0.15}
          strokeWidth={3}
        />
      </Svg>
    </Animated.View>
  );
}

/** "You're going": the green tick. */
function DoneTick() {
  return (
    <Svg width={ICON} height={ICON} viewBox="0 0 21 21" fill="none">
      <Circle cx={10.45} cy={10.45} r={10.45} fill={colors.success} />
      <Path
        d="M6.1 10.98L8.84 13.64L14.8 7.84"
        stroke="#fff"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** A prepared checkout that stopped before Pay: finished, in the button's
 *  blue — never the green that means tickets were issued. */
function ReadyMark({ color }: { color: string }) {
  return (
    <Svg width={ICON} height={ICON} viewBox="0 0 21 21" fill="none">
      <Circle cx={10.45} cy={10.45} r={9.45} stroke={color} strokeWidth={2} />
      <Path
        d="M6.6 10.9L9 13.2L14.3 8"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Svg>
  );
}

/** Nothing more will happen without the buyer. */
function StoppedMark({ color }: { color: string }) {
  return (
    <Svg width={ICON} height={ICON} viewBox="0 0 21 21" fill="none">
      <Circle cx={10.45} cy={10.45} r={9.45} stroke={color} strokeWidth={2} />
      <Path
        d="M8.2 7.2v6.5M12.7 7.2v6.5"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
      />
    </Svg>
  );
}

const styles = StyleSheet.create({
  button: {
    alignItems: "center",
    borderRadius: RADIUS,
    flexDirection: "row",
    gap: 10,
    justifyContent: "center",
    minHeight: CONTROL_HEIGHT,
    paddingHorizontal: 16,
  },
  fill: {
    ...StyleSheet.absoluteFill,
    backgroundColor: colors.primary,
    borderRadius: RADIUS,
  },
  morph: { flexShrink: 1 },
  words: { fontSize: 15, fontWeight: "600" },
});
